<template>
    <div class="snoop-modal-wrapper" @click="showGlossary">
        <div ref="preBoundary" tabindex="0" />
        <div
            ref="modal"
            class="snoop-modal qa-cookie-consent"
            aria-modal="true"
            tabindex="-1"
            role="dialog"
        >
            <snoop-intro
                v-if="currentLayer === 'intro'"
                :translations="translations.pages.intro"
                :optional-message="optionalMessage"
                @acceptAll="acceptAll"
                @acceptTechnicallyRequired="acceptTechnicallyRequired"
                @showSettings="currentLayer = 'settings'"
            />

            <snoop-settings
                v-if="currentLayer === 'settings'"
                :translations="translations"
                :cookie-config="cookieConfig"
                :accepted-cookie-ids="acceptedCookieIds"
                :scroll-to="scrollTo"
                @close="currentLayer = 'intro'"
                @acceptAll="acceptAll"
                @acceptTechnicallyRequired="acceptTechnicallyRequired"
                @acceptSelected="$emit('save', acceptedCookieIds)"
            />

            <snoop-glossary
                v-if="currentLayer === 'glossary'"
                :translations="translations.pages.glossary"
                :term-id="glossaryTermId"
                @close="currentLayer = 'intro'"
            />

            <snoop-cookie-consent-footer :translations="translations" />
        </div>
        <div ref="postBoundary" tabindex="0" />
    </div>
</template>

<script>
import { clone, concat, uniq } from 'ramda'

import { getAvailableCookieIds, getTechnicallyRequiredCookieIds } from '../util/userSettings'
import SnoopCookieConsentFooter from './CookieConsentFooter.vue'
import SnoopIntro from './Intro.vue'
import SnoopGlossary from './Glossary.vue'
import SnoopSettings from './Settings.vue'


/**
 * Finds all keyboard-focusable elements within a specified element.
 *
 * @param {HTMLElement} [root=document] root The root element from where to query.
 * @returns {Array}
 * @see https://zellwk.com/blog/keyboard-focusable-elements/
 */
const getKeyboardFocusableElements = (root = document) => [...root.querySelectorAll(
    'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])',
)]
    .filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'))

export default {
    components: { SnoopCookieConsentFooter, SnoopIntro, SnoopGlossary, SnoopSettings },

    props: {
        optionalMessage: { type: String, required: true },
        translations: { type: Object, required: true },
        cookieConfig: { type: Object, required: true },
        userSelection: { type: Array, required: true },
        acceptedCookieNames: { type: Array, required: true },
        scrollTo: { type: String, default: '' },
        hideOnStart: { type: Boolean, default: false },
    },

    data: () => ({
        acceptedCookieIds: [],
        currentLayer: 'intro',
        glossaryTermId: null,
    }),

    created () {
        // Prevent in place manipulation of parent values
        this.acceptedCookieIds = uniq(concat(
            clone(this.userSelection),
            getTechnicallyRequiredCookieIds(this.cookieConfig),
        ))
    },

    mounted () {
        // Focus root for wcag reasons
        this.$refs.modal.focus()

        // If a specific cookie is targeted for scrolling, the layer is set to 'settings'. The scrollTo property is
        // propagated to 'Settings.vue' where it scrolls to the corresponding cookie section.
        if (this.scrollTo) {
            this.currentLayer = 'settings'
        }

        setTimeout(() => {
            document.addEventListener('keydown', this.onKeydown)
        }, 500)
        document.addEventListener('focusout', this.onFocusout)
    },

    destroyed () {
        document.removeEventListener('keydown', this.onKeydown)
        document.removeEventListener('focusout', this.onFocusout)
    },

    methods: {
        /**
         * Make sure the focus stays on the modal if opened.
         * Focus last focusable element if focus goes to preBoundary,
         * else focus first focusable element or the modal as fallback.
         *
         * See https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
         */
        onFocusout (event) {
            if (event.relatedTarget && this.$refs.modal && !this.$refs.modal.contains(event.relatedTarget)) {
                const els = getKeyboardFocusableElements(this.$refs.modal)
                const idx = event.relatedTarget === this.$refs.preBoundary ? els.length - 1 : 0
                const nextEl = els[idx] || this.$refs.modal
                nextEl.focus()
            }
        },

        acceptAll () {
            this.acceptedCookieIds = getAvailableCookieIds(this.cookieConfig)
            this.$emit('save', this.acceptedCookieIds)
        },

        acceptTechnicallyRequired () {
            this.acceptedCookieIds = getTechnicallyRequiredCookieIds(this.cookieConfig)
            this.$emit('save', this.acceptedCookieIds)
        },

        onKeydown (event) {
            // Only on Esc
            if (event.keyCode !== 27) return

            if (this.hideOnStart || this.acceptedCookieNames.includes(this.cookieConfig.consentCookie.name)) {
                this.$emit('close')
            }
        },

        /**
         * If the clicked element or its ancestor has the class 'js-open-glossary', prevent the default action,
         * open the glossary view and scroll to the glossary term.
         */
        showGlossary (event) {
            if (event.target.closest('.js-open-glossary')) {
                event.preventDefault()
                this.currentLayer = 'glossary'
                this.glossaryTermId = event.target.closest('.js-open-glossary').getAttribute('data-term-id') || null
            }
        },
    },
}
</script>
